import React, { useCallback } from 'react'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  Dialog,
  Input,
  Select,
  Typography,
  useInput,
  useSelect,
  useViewport,
  DatePicker,
  useDatePicker,
  formatDate
} from '@chilecompra/react-kit'
import { GridContainer, GridItem, RootContainer, ExclamationIcon, Button } from './EditParticipantModal.styles'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  onSetEditModalShowSuccess,
  onSendParticipantDataEditedThunk,
  onSetEditModalFunctionSuccess,
  onSetEditStartDateSuccess,
  onSetEditEndDateSuccess
} from './EditParticipantModal.actions'
import { onGetFunctionByProcessThunk } from '../ManageParticipants/ManageParticipants.actions'
import { PUBLIC_URL } from '../../config/settings/environment'
import {
  checkInputNationalDniError,
  checkInputMaxDateTyping,
  checkInputMinDateTyping
} from '../../modules/utils/checkers'
import { onGetParticipantsListThunk } from '../ListParticipants/ListParticipants.actions'
import { useParams } from 'react-router-dom'
import { PATTERN_DATE_OUT, PATTERN_TIME_START_OUT, PATTERN_TIME_END_OUT } from '../../config/settings/constants'

/**
 * The Edit Participant modal container.
 */
const EditParticipantModalContainer = () => {
  const { processId, fromWhere } = useParams()
  const dispatch = useDispatch()
  const { process, functions } = useSelector(state => state.manageParticipants)
  const {
    size: { isTiny, isSmall, isDownToMedium }
  } = useViewport()

  const {
    loading,
    showEditParticipantModal,
    fullName,
    role,
    dni,
    email,
    isForeignPerson,
    functionId: functionIdSelected,
    processId: processIdSelected,
    participantId: selectedParticipantId,
    participantDetailId,
    originalProcessId,
    originalFunctionId,
    startDate,
    endDate,
    isManual,
    participantProcessId
  } = useSelector(state => state.editParticipantModal)

  const handleCloseModal = () => {
    dispatch(onSetEditModalShowSuccess({ showEditParticipantModal: false }))
  }

  const {
    value: fullNameValue,
    onChange: onChangeFullNameValue,
    error: errorFullNameValue
  } = useInput({
    initialValue: fullName || '',
    errorCallbacks: [checkInputErrorRequired('Debes ingresar el nombre completo.')],
    overwriteCallback: () => fullName
  })

  const {
    value: roleValue,
    onChange: onChangeRoleValue,
    error: errorRoleValue
  } = useInput({
    initialValue: role || '',
    errorCallbacks: [checkInputErrorRequired('Debes ingresar el nombre completo.')],
    overwriteCallback: () => role
  })

  const {
    value: dniValue,
    onChange: onChangeDniValue,
    error: errorDniValue
  } = useInput({
    initialValue: dni || '',
    errorCallbacks: isForeignPerson
      ? [checkInputErrorRequired('Debes ingresar un RUT')]
      : [checkInputErrorRequired('Debes ingresar un RUT'), checkInputNationalDniError],
    overwriteCallback: () => dni
  })

  const {
    value: emailValue,
    onChange: onChangeEmailValue,
    error: errorEmailValue
  } = useInput({
    initialValue: email || '',
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    overwriteCallback: () => email
  })

  const {
    value: selectedProcessStage,
    onChange: onChangeProcessStage,
    error: errorChangeProcessStage
  } = useSelect({
    initialValue: processIdSelected || '',
    overwriteCallback: () => processIdSelected,
    changeCallback: updatedValue => {
      resetSelectedFunction()
      batch(() => {
        dispatch(onSetEditModalFunctionSuccess({ functionId: '' }))
        dispatch(onGetFunctionByProcessThunk(updatedValue))
      })
    },
    errorCallbacks: [checkInputErrorRequired('Debes seleccionar un proceso')]
  })

  const {
    value: selectedFunctionStage,
    onChange: onChangeFunction,
    error: errorChangeFunction,
    onReset: resetSelectedFunction
  } = useSelect({
    initialValue: functionIdSelected || '',
    overwriteCallback: () => functionIdSelected,
    errorCallbacks: [checkInputErrorRequired('Debes seleccionar una función')]
  })

  const {
    error: errorDateStart,
    value: dateStartValue,
    onChange: handleDateStartChange
  } = useDatePicker({
    overwriteCallback: () => startDate,
    initialValue: startDate || '',
    errorCallbacks: !endDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha inicio de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha inicio de función'),
          checkInputMaxDateTyping(new Date(endDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetEditStartDateSuccess({
          startDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_START_OUT}`).replace(
            ' ',
            'T'
          )
        })
      )
  })

  const {
    error: errorDateEnd,
    value: dateEndValue,
    onChange: handleDateEndChange
  } = useDatePicker({
    overwriteCallback: () => endDate,
    initialValue: endDate || '',
    errorCallbacks: !startDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha fin de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha fin de función'),
          checkInputMinDateTyping(new Date(startDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetEditEndDateSuccess({
          endDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_END_OUT}`).replace(' ', 'T')
        })
      )
  })

  const handleCallbackExcecute = () => {
    const payload = {
      processNumber: processId,
      fromWhere
    }
    dispatch(onGetParticipantsListThunk(payload))
    dispatch(
      onSetEditModalShowSuccess({
        showEditParticipantModal: false,
        participantSelected: '',
        fullName: '',
        role: '',
        dni: '',
        email: '',
        isForeignPerson: '',
        participantId: '',
        participantDetailId: '',
        functionId: '',
        processId: '',
        originalProcessId: '',
        originalFunctionId: '',
        startDate: '',
        endDate: '',
        isManual: null,
        participantProcessId: ''
      })
    )
  }

  const handleSubmit = () => {
    const selectedProcess = process.find(({ value }) => value === selectedProcessStage)
    const selectedFunction = functions.find(({ value }) => value === selectedFunctionStage)
    const payload = {
      fullNameValue,
      roleValue,
      dniValue,
      emailValue,
      selectedProcess,
      selectedFunction,
      participantId: selectedParticipantId,
      participantDetailId,
      onSuccessCallback: handleCallbackExcecute,
      isForeignPerson,
      functionProcessChanged:
        originalProcessId !== selectedProcessStage || originalFunctionId !== selectedFunctionStage,
      startDate,
      endDate,
      isManual,
      participantProcessId
    }
    dispatch(onSendParticipantDataEditedThunk(payload))
  }

  const canNotSubmit = useCallback(() => {
    const haveSomeThing =
      dniValue !== '' &&
      fullNameValue !== '' &&
      emailValue !== '' &&
      roleValue !== '' &&
      selectedProcessStage !== '' &&
      dateStartValue !== '' &&
      dateEndValue !== '' &&
      selectedFunctionStage !== ''
    const haveError =
      errorDateStart !== '' ||
      errorDateEnd !== '' ||
      errorDniValue !== '' ||
      errorFullNameValue !== '' ||
      errorEmailValue !== '' ||
      errorRoleValue !== '' ||
      errorChangeProcessStage !== '' ||
      errorChangeFunction !== ''
    return !haveSomeThing || haveError
  }, [
    dateStartValue,
    dateEndValue,
    dniValue,
    emailValue,
    fullNameValue,
    roleValue,
    selectedProcessStage,
    selectedFunctionStage,
    errorDateEnd,
    errorDateStart
  ])

  const disabledSubmit = canNotSubmit()

  return (
    <Dialog
      open={showEditParticipantModal}
      onClose={handleCloseModal}
      margin="0 20px"
      padding="0"
      maxWidth="750px"
      disableBackdropClick
    >
      <RootContainer justifyContent="center">
        <GridItem xs={12} container justifyContent="center">
          <ExclamationIcon src={`${PUBLIC_URL}/assets/images/alert_icon.svg`} />
        </GridItem>
        <GridItem xs={12} padding="37px 0 0">
          <Typography fontSize="18px" color="black1" fontWeight="bold" variant="h3" align="center">
            Editar información del participante
          </Typography>
        </GridItem>
        <GridItem xs={12} md={8} padding="27px 0 0">
          <Typography
            fontSize={isDownToMedium ? '14px' : '16px'}
            color="black1"
            fontWeight="regular"
            variant="h3"
            align={isDownToMedium ? 'left' : 'center'}
          >
            Puedes realizar edición de información en el caso de participantes externos a Mercado Público.
          </Typography>
        </GridItem>
        <GridItem container item xs={12} justifyContent="center">
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '40px 20px 10px 0' : '20px 0 0'}
          >
            <Input
              disabled
              info=""
              error={errorFullNameValue}
              label="Participante"
              value={fullNameValue}
              onChange={onChangeFullNameValue}
            />
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '40px 0 10px 0' : '0'}
          >
            <Input
              disabled={loading}
              info=""
              error={errorRoleValue}
              label="Cargo"
              value={roleValue}
              onChange={onChangeRoleValue}
            />
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '10px 20px 10px 0' : '0'}
          >
            <Input disabled info="" error={errorDniValue} label="RUT" value={dniValue} onChange={onChangeDniValue} />
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={!isTiny && !isSmall ? '10px 0' : '0'}>
            <Input
              disabled={loading}
              info=""
              label="Correo electrónico"
              placeholder=""
              value={emailValue}
              onChange={onChangeEmailValue}
              error={errorEmailValue}
            />
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '10px 20px 10px 0' : '0'}
          >
            <Select
              disabled
              label="Etapa del proceso"
              value={selectedProcessStage}
              options={process || []}
              placeholder="Selecciona"
              onChange={onChangeProcessStage}
              error={errorChangeProcessStage}
            />
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={!isTiny && !isSmall ? '10px 0' : '0'}>
            <Select
              disabled
              label="Función"
              value={selectedFunctionStage}
              options={functions || []}
              placeholder="Selecciona"
              onChange={onChangeFunction}
              error={errorChangeFunction}
            />
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '10px 20px 10px 0' : '0'}
          >
            <DatePicker
              name="dateStartValue"
              error={errorDateStart}
              label="Fecha inicio de función"
              value={dateStartValue}
              maxDate={dateEndValue}
              onChange={handleDateStartChange}
              width="100%"
            />
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={!isTiny && !isSmall ? '10px 0' : '0'}>
            <DatePicker
              name="dateEndValue"
              error={errorDateEnd}
              label="Fecha fin de función"
              value={dateEndValue}
              minDate={dateStartValue}
              onChange={handleDateEndChange}
              width="100%"
            />
          </GridItem>
        </GridItem>
        <GridItem xs={12} padding="44px 0 0">
          <GridContainer justifyContent="center">
            <GridItem xs={12} container justifyContent="center">
              <Button disabled={loading || disabledSubmit} loading={loading} onClick={handleSubmit} color="primary">
                Guardar
              </Button>
            </GridItem>
            <GridItem xs={12} container justifyContent="center" padding="10px 0">
              <Button onClick={handleCloseModal} color="default" variant="text">
                Cancelar
              </Button>
            </GridItem>
          </GridContainer>
        </GridItem>
      </RootContainer>
    </Dialog>
  )
}

export default EditParticipantModalContainer
